import { InfoOutlined } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete, Box, CircularProgress, Grid, InputAdornment, Popper, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { INVENTORY_MANAGE_TYPE, PART_TYPES } from '../../../utility/Constant';
import { NumericEngine } from '../../../utility/helpers/numericEngine';
import BatchSelectionModal from '../../Inventory/BatchSelectionModal';
import { formatNumberAsWhole } from '../../../utility/helpers/commonFunction';

const GenralInventoryAutocomplete = ({
    loader,
    setIsUsedInvoiceQty,
    setOutOfStockStatus,
    setDisabledFields,
    setOpenTrackedIncreaseModal,
    usedQuantityOfInventory,
    finalizedInventoryCount,
    usedBatchQty,
    inventoryPageData,
    inventoryList,
    handleOpenPartModal,
    serviceList,
    outerIndex,
    innerIndex,
    setServiceList,
    setSaveIndexes,
    setSelectedInventory,
    setInventoryModalType,
    setInventoryTabIndex,
    setShowAllInventoryTabs,
    searchInventory,
    setSearchInventory,
    setBatchNumber,
    setAddQty,
    setSelectedInventoryId,
    setOpenpartModel,
    partQtyRef,
    onTabNavigation,
    // TanStack Query infinite scroll props
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    /*
     * Estimates and work orders take **unmanaged parts only** (client, 2026-09-24): no inventory
     * dropdown, no batch selection, no "+ MANUALLY" inventory modal — the user just types a part name.
     * The managed-inventory code below is untouched and comes back by turning this off.
     * In this mode the part name is a plain text field, not the Autocomplete, so the browser's own
     * autofill of earlier part names works (the Autocomplete forces autocomplete="off").
     */
    unmanagedOnly = false,
}) => {
    const [inventoryId, setInventoryId] = useState(null);
    const [openBatchSelectionModal, setBatchSelectionModal] = useState(false);

    const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);
    const loadingRef = useRef(false);
    const scrollTimeoutRef = useRef(null);

    // Keep track of the listbox element and scroll position
    const listboxRef = useRef(null);
    const scrollPositionRef = useRef(0);
    const prevInventoryLengthRef = useRef(0);

    // This prevents duplicate loading of data
    useEffect(() => {
        loadingRef.current = loader;
    }, [loader]);

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            if (searchDebounceTimer) {
                clearTimeout(searchDebounceTimer);
            }
        };
    }, [searchDebounceTimer]);

    // Track changes to inventoryList and restore scroll position if needed
    useEffect(() => {
        if (inventoryList?.length > prevInventoryLengthRef.current && listboxRef.current) {
            // We only want to restore the scroll position when new items are added (not on initial load)
            if (prevInventoryLengthRef.current > 0) {
                // Set a small timeout to ensure DOM has updated with new items
                setTimeout(() => {
                    if (listboxRef.current) {
                        listboxRef.current.scrollTop = scrollPositionRef.current;
                    }
                }, 0);
            }
        }
        prevInventoryLengthRef.current = inventoryList?.length || 0;
    }, [inventoryList]);

    const draftPartName = serviceList[outerIndex]?.parts[innerIndex]?.partName ?? '';

    // Runs on every keystroke, so copy only the edited service and part instead of deep-cloning the whole list
    const updateDraftPartName = (newInputValue) => {
        const service = serviceList[outerIndex];
        if (!service?.parts?.[innerIndex]) return;

        const parts = [...service.parts];
        parts[innerIndex] = { ...parts[innerIndex], partName: newInputValue };
        const cloneService = [...serviceList];
        cloneService[outerIndex] = { ...service, parts };
        setServiceList(cloneService);
    };

    const handleInputChange = (event, newInputValue, reason) => {
        if (reason === 'reset') {
            return;
        }

        if (reason === 'input') {
            updateDraftPartName(newInputValue);

            if (searchDebounceTimer) {
                clearTimeout(searchDebounceTimer);
            }

            const timer = setTimeout(() => {
                setSearchInventory(newInputValue);
            }, 300);
            setSearchDebounceTimer(timer);
        }
    };

    // Custom scroll handler for infinite scroll with TanStack Query
    const handleScroll = (event) => {
        const listboxNode = event.currentTarget;

        // Store the current listbox element and scroll position
        listboxRef.current = listboxNode;
        scrollPositionRef.current = listboxNode.scrollTop;

        const scrollPosition = listboxNode.scrollTop;
        const scrollBottom = scrollPosition + listboxNode.clientHeight >= listboxNode.scrollHeight - 20; // Add a small threshold

        if (
            scrollBottom &&
            hasNextPage &&
            !isFetchingNextPage &&
            !loadingRef.current // Prevent multiple simultaneous loading
        ) {
            // Clear any existing timeout
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }

            // Add a small delay to prevent rapid fire API calls
            scrollTimeoutRef.current = setTimeout(() => {
                if (!isFetchingNextPage && hasNextPage) {
                    fetchNextPage();
                }
                scrollTimeoutRef.current = null;
            }, 150);
        }
    };

    const handleSelectPart = (option) => {
        if (!option?._id) {
            toast.error('Invalid inventory item.');
            return;
        }

        // Case 3 - Tracked Inventory - 0 in stock
        const inStock = Number(option.inStock) + Number(finalizedInventoryCount[option._id] || 0) - Number(usedQuantityOfInventory[option._id] || 0);
        if (inStock <= 0) {
            if (option?.isTracked) {
                setOutOfStockStatus('create');
                setDisabledFields(['partName', 'partNumber', 'supplierId']);

                setShowAllInventoryTabs(false);

                setBatchNumber(option?.batchNumber[0]);
                setSelectedInventoryId(option?._id);
                setSaveIndexes({ outerIndex, innerIndex });
                setIsUsedInvoiceQty(0);
                setOpenpartModel(true);
                // setOpenTrackedIncreaseModal(true);
                return;
            }
            toast.error('You do not have sufficient quantity in your inventory. Please add additional quantity to continue', {
                icon: <InfoOutlined sx={{ fontSize: '22px' }} />,
            });
            if (option.batchNumber?.length > 0 && option.batchNumber[0]) setBatchNumber(option.batchNumber[0]);
            setSaveIndexes({ outerIndex: outerIndex, innerIndex: innerIndex });
            setSelectedInventoryId(option?._id || option?.inventoryId || option?.inventory?._id);
            setAddQty(true);
            setIsUsedInvoiceQty(0);
            setOpenpartModel(true);
            return;
        }

        let cloneService = JSON.parse(JSON.stringify(serviceList));
        if (cloneService[outerIndex] && cloneService[outerIndex].parts && cloneService[outerIndex].parts[innerIndex]) {
            const alreadyExists = cloneService[outerIndex].parts.some((part, index) => part?._id === option._id && index !== innerIndex);

            if (alreadyExists) {
                cloneService[outerIndex].parts[innerIndex].partName = '';
                setServiceList(cloneService);
                setSearchInventory('');
                toast.error('This part is already added.');
                return;
            }
            if (option.batchNumber.length > 1) {
                if (option?.batchArray?.length) {
                    delete option.batchArray;
                }

                setInventoryId(option);
                setBatchSelectionModal(true);
                return;
            }

            const partRowId = cloneService[outerIndex].parts[innerIndex].partRowId;
            delete cloneService[outerIndex].parts[innerIndex].type;
            const partQty = cloneService[outerIndex].parts[innerIndex].partQty > inStock ? inStock : cloneService[outerIndex].parts[innerIndex].partQty;
            const partCost = NumericEngine.format(option?.lastRetailPrice || 0);
            // const previousPartCost = cloneService[outerIndex].parts[innerIndex].partCost;

            cloneService[outerIndex].parts[innerIndex] = { ...option, ...(partRowId ? { partRowId } : {}) };
            cloneService[outerIndex].parts[innerIndex].partQty = partQty || 1;
            // cloneService[outerIndex].parts[innerIndex].partCost = previousPartCost && previousPartCost > 0 ? previousPartCost : partCost || 0;
            cloneService[outerIndex].parts[innerIndex].partCost = partCost || 0;

            cloneService[outerIndex].parts[innerIndex].description = option?.lastDescription || '';
            // const result = partQty * (previousPartCost && previousPartCost > 0 ? previousPartCost : partCost || 0);
            const result = NumericEngine.multiply(partQty || 0, partCost || 0);
            const totalAmount = result;
            cloneService[outerIndex].parts[innerIndex].batchArray = [
                {
                    batchNumber: option.batchNumber[0],
                    batchQty: partQty,
                    outerIndex,
                    innerIndex,
                    batchIndex: 0,
                    inStock: partQty,
                },
            ];

            cloneService[outerIndex].parts[innerIndex].totalAmount = totalAmount;
        } else {
            // console.log("Invalid indices provided.");
        }
        // Update the state with the modified array
        setSearchInventory('');
        setServiceList(cloneService);
    };

    /*
     * Unmanaged only: a plain text field instead of the MUI Autocomplete. The Autocomplete always sets
     * autocomplete="off" on its input, which hides the browser's own autofill of part names typed before
     * (client asked for it back). The ids match the Autocomplete's input so Chrome's saved entries still apply.
     */
    if (unmanagedOnly) {
        const commitUnmanagedPart = (defaultQtyAndCost) => {
            const cloneService = JSON.parse(JSON.stringify(serviceList));
            const currentPart = cloneService[outerIndex]?.parts?.[innerIndex];
            if (!currentPart) return;
            currentPart.inventoryType = INVENTORY_MANAGE_TYPE.UNMANAGED;
            // A new line starts at qty 1 and cost 0; a line that already has them keeps them.
            if (defaultQtyAndCost) {
                if (currentPart.partQty === undefined || currentPart.partQty === null || currentPart.partQty === '') currentPart.partQty = 1;
                if (currentPart.partCost === undefined || currentPart.partCost === null || currentPart.partCost === '') currentPart.partCost = 0;
                const qty = Number(currentPart.partQty) || 0;
                const cost = Number(currentPart.partCost) || 0;
                currentPart.totalAmount = qty && cost ? Number(NumericEngine.multiply(qty, cost)) : 0;
            }
            setServiceList(cloneService);
        };

        return (
            <TextField
                fullWidth
                variant="standard"
                id="standard-search"
                placeholder="Part name"
                value={draftPartName}
                onChange={(event) => updateDraftPartName(event.target.value)}
                onBlur={() => commitUnmanagedPart(false)}
                onKeyDown={(event) => {
                    // Enter must never submit the page form; Tab moves on to the quantity like the Autocomplete did.
                    if (event.key === 'Enter' || (event.key === 'Tab' && !event.shiftKey && draftPartName.trim())) {
                        event.preventDefault();
                        event.stopPropagation();
                        if (draftPartName.trim()) {
                            commitUnmanagedPart(true);
                            if (onTabNavigation) {
                                onTabNavigation();
                            }
                        }
                    }
                }}
                InputProps={{ id: 'standard-adornment-amount' }}
                inputProps={{ autoComplete: 'on' }}
            />
        );
    }

    return (
        <>
            <Autocomplete
                fullWidth
                sx={{
                    '& .MuiInputBase-root': {
                        paddingRight: ' 0 !important',
                    },
                }}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                id={`inventory-search-${outerIndex}-${innerIndex}`}
                options={
                    unmanagedOnly
                        ? [] // unmanaged only: nothing to pick from, the typed name is the part
                        : inventoryList?.length > 0
                          ? isFetchingNextPage
                              ? [...inventoryList, 'loading'] // Add loading indicator when fetching next page
                              : inventoryList
                          : []
                }
                getOptionLabel={(option) => (option === 'loading' ? '' : option?.part?.partName)}
                loading={unmanagedOnly ? false : loader}
                freeSolo
                noOptionsText={false}
                onFocus={() => {
                    let cloneService = JSON.parse(JSON.stringify(serviceList));
                    if (cloneService[outerIndex] && cloneService[outerIndex].parts && cloneService[outerIndex].parts[innerIndex]?.partName) {
                        setSearchInventory(cloneService[outerIndex].parts[innerIndex].partName);
                    }
                }}
                inputValue={draftPartName}
                onBlur={() => {
                    let cloneService = JSON.parse(JSON.stringify(serviceList));

                    if (cloneService[outerIndex] && cloneService[outerIndex].parts && cloneService[outerIndex].parts[innerIndex]) {
                        cloneService[outerIndex].parts[innerIndex].inventoryType = INVENTORY_MANAGE_TYPE.UNMANAGED;
                        const currentPartName = cloneService[outerIndex].parts[innerIndex].partName ?? '';
                        if (currentPartName.trim()) {
                            cloneService[outerIndex].parts[innerIndex].partName = currentPartName;
                        }
                        setServiceList(cloneService);
                    }

                    setSearchInventory('');
                    // Reset the scroll position tracking when closing dropdown
                    scrollPositionRef.current = 0;
                    prevInventoryLengthRef.current = 0;

                    // Clear any pending scroll timeouts
                    if (scrollTimeoutRef.current) {
                        clearTimeout(scrollTimeoutRef.current);
                        scrollTimeoutRef.current = null;
                    }
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Tab' || event.key === 'Enter') {
                        event.preventDefault();
                        event.stopPropagation();
                        if (
                            draftPartName &&
                            draftPartName.trim()
                            // && !inventoryList?.length > 0
                        ) {
                            const highlightedOption = document.querySelector('li.MuiAutocomplete-option.Mui-focused');

                            if (highlightedOption) {
                                // Get the data-option-index attribute
                                const optionIndex = highlightedOption.getAttribute('data-option-index');
                                if (optionIndex !== null && inventoryList[optionIndex]) {
                                    handleSelectPart(inventoryList[optionIndex]);
                                }
                                // Move focus to quantity field after selection
                                if (onTabNavigation) {
                                    onTabNavigation();
                                }
                                return;
                            }
                            let cloneService = JSON.parse(JSON.stringify(serviceList));
                            cloneService[outerIndex].parts[innerIndex].partQty = 1;
                            cloneService[outerIndex].parts[innerIndex].partCost = 0;
                            cloneService[outerIndex].parts[innerIndex].partName = draftPartName;
                            cloneService[outerIndex].parts[innerIndex].inventoryType = INVENTORY_MANAGE_TYPE.UNMANAGED;
                            const totalAmount = 0;

                            cloneService[outerIndex].parts[innerIndex].totalAmount = totalAmount;
                            setServiceList(cloneService);
                            setSearchInventory('');
                            // Move focus to quantity field after setting as unmanaged
                            if (onTabNavigation) {
                                onTabNavigation();
                            }
                        } else {
                            const highlightedOption = document.querySelector('li.MuiAutocomplete-option.Mui-focused');

                            if (highlightedOption) {
                                // Get the data-option-index attribute
                                const optionIndex = highlightedOption.getAttribute('data-option-index');
                                if (optionIndex !== null && inventoryList[optionIndex]) {
                                    // Call handleSelectPart with the highlighted option
                                    handleSelectPart(inventoryList[optionIndex]);
                                    // Move focus to quantity field after selection
                                    if (onTabNavigation) {
                                        onTabNavigation();
                                    }
                                }
                            }
                        }
                    }
                }}
                ListboxProps={{
                    style: {
                        maxHeight: '300px',
                        overflowY: 'auto',
                        scrollBehavior: 'auto', // Changed from 'smooth' to prevent flickering
                    },
                    onScroll: handleScroll,
                    // Get a reference to the listbox when it's created
                    ref: (node) => {
                        if (node) {
                            listboxRef.current = node;
                        }
                    },
                }}
                onInputChange={handleInputChange}
                filterOptions={(x) => x}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        variant="standard"
                        id="standard-search"
                        placeholder={unmanagedOnly ? 'Part name' : 'Search from inventory'}
                        InputProps={{
                            ...params.InputProps,
                            id: 'standard-adornment-amount',

                            startAdornment: unmanagedOnly ? null : (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                            endAdornment: unmanagedOnly ? null : (
                                <InputAdornment position="end">
                                    <Typography
                                        color="primary"
                                        fontWeight={500}
                                        fontSize="15px"
                                        onClick={() => {
                                            setSaveIndexes({
                                                outerIndex: outerIndex,
                                                innerIndex: innerIndex,
                                            });
                                            const ids = serviceList[outerIndex].parts.filter((item) => item?._id);
                                            setSelectedInventory(ids);
                                            setShowAllInventoryTabs(true); // Open this when ti show the listing tab in inventory modal.
                                            setInventoryTabIndex(0);
                                            setInventoryModalType('add');
                                            setIsUsedInvoiceQty(1);
                                            handleOpenPartModal(true);
                                        }}
                                        sx={{ cursor: 'pointer', fontSize: '12px!important' }}
                                    >
                                        + MANUALLY
                                    </Typography>
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
                disablePortal={true}
                renderOption={(props, option) => {
                    // Show loading indicator at the end of the list when fetching next page
                    if (option === 'loading') {
                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    padding: '12px',
                                }}
                            >
                                <CircularProgress size={20} />
                            </div>
                        );
                    }

                    if (!loader) {
                        return (
                            <>
                                <Box
                                    component="li"
                                    {...props}
                                    style={{ padding: 0 }}
                                    sx={{
                                        // '&.MuiAutocomplete-option:hover': {
                                        //     backgroundColor: 'rgba(33, 150, 243, 0.04) !important',
                                        // },
                                        '&.MuiAutocomplete-option.Mui-focused': {
                                            backgroundColor: 'rgba(33, 150, 243, 0.04) !important',
                                        },
                                        '&.MuiAutocomplete-option[aria-selected="true"]': {
                                            backgroundColor: 'rgba(33, 150, 243, 0.04) !important',
                                        },
                                    }}
                                >
                                    <li
                                        {...props}
                                        style={{
                                            width: '100%',
                                            padding: '0',
                                        }}
                                        key={option?._id}
                                    >
                                        <Grid
                                            container
                                            padding={'12px'}
                                            alignItems={'center'}
                                            sx={{
                                                height: '100%',

                                                cursor: 'pointer',
                                                transition: 'background-color 0.3s ease',
                                                width: '100%',
                                                overflow: 'hidden',
                                            }}
                                            onClick={() => {
                                                handleSelectPart(option);
                                            }}
                                        >
                                            <Grid xs={4} md={4}>
                                                <Stack direction={'column'} gap={0.5}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontWeight="600"
                                                        letterSpacing={'0.15px'}
                                                        padding={0}
                                                        lineHeight={'normal'}
                                                        style={{ wordBreak: 'break-word' }}
                                                        sx={{
                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                        }}
                                                    >
                                                        {option?.partsType === PART_TYPES.TYRE ? option?.tyreDimension : option?.part?.partName}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        padding={0}
                                                        sx={{
                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                        }}
                                                        lineHeight={'normal'}
                                                        letterSpacing={'0.17px'}
                                                    >
                                                        {option?.partsType === PART_TYPES.PARTS ? option?.partNumber : option?.part?.partName}
                                                    </Typography>
                                                </Stack>
                                            </Grid>

                                            <Grid xs={4} md={4}>
                                                <Stack direction={'column'} gap={0.5}>
                                                    <Typography
                                                        padding={0}
                                                        sx={{
                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                            letterSpacing: '0.17px',
                                                            fontSize: '14px',
                                                            fontWeight: 400,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            minHeight: '20px',
                                                        }}
                                                        lineHeight={'normal'}
                                                    >
                                                        <div style={{ width: '20px', height: '20px' }}>
                                                            {' '}
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                                <path
                                                                    d="M0.625 6.24967H8.75L9.375 7.49967H1.25L0.625 6.24967ZM1.45833 8.74967H9.58333L10.2083 9.99967H2.08333L1.45833 8.74967ZM15 15.4163C15.6917 15.4163 16.25 14.858 16.25 14.1663C16.25 13.4747 15.6917 12.9163 15 12.9163C14.3083 12.9163 13.75 13.4747 13.75 14.1663C13.75 14.858 14.3083 15.4163 15 15.4163ZM16.25 7.91634H14.1667V9.99967H17.8833L16.25 7.91634ZM6.66667 15.4163C7.35833 15.4163 7.91667 14.858 7.91667 14.1663C7.91667 13.4747 7.35833 12.9163 6.66667 12.9163C5.975 12.9163 5.41667 13.4747 5.41667 14.1663C5.41667 14.858 5.975 15.4163 6.66667 15.4163ZM16.6667 6.66634L19.1667 9.99967V14.1663H17.5C17.5 15.5497 16.3833 16.6663 15 16.6663C13.6167 16.6663 12.5 15.5497 12.5 14.1663H9.16667C9.16667 15.5497 8.04167 16.6663 6.66667 16.6663C5.28333 16.6663 4.16667 15.5497 4.16667 14.1663H2.5V11.2497H4.16667V12.4997H4.8C5.25833 11.9913 5.925 11.6663 6.66667 11.6663C7.40833 11.6663 8.075 11.9913 8.53333 12.4997H12.5V4.99967H2.5C2.5 4.07467 3.24167 3.33301 4.16667 3.33301H14.1667V6.66634H16.6667Z"
                                                                    fill="#006CDD"
                                                                />
                                                            </svg>
                                                        </div>
                                                        {option?.supplier?.name}
                                                    </Typography>
                                                    <Typography
                                                        padding={0}
                                                        sx={{
                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                            letterSpacing: '0.17px',
                                                            fontSize: '14px',
                                                            fontWeight: 400,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            minHeight: '20px',
                                                        }}
                                                        lineHeight={'normal'}
                                                    >
                                                        {' '}
                                                        <div style={{ width: '20px', height: '20px' }}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                                <g clip-path="url(#clip0_10991_55187)">
                                                                    <path
                                                                        d="M4.48218 5.60742H7.53739V6.8902H4.48218V5.60742ZM4.48218 8.52439H7.53739V9.80717H4.48218V8.52439ZM4.48218 11.4413H7.53739V12.7241H4.48218V11.4413ZM4.48218 14.3583H7.53739V15.6411H4.48218V14.3583Z"
                                                                        fill="#006CDD"
                                                                    />
                                                                    <path
                                                                        d="M10.6607 17.3494V6.86787L17.359 6.86005V13.9309H18.641V6.21791C18.641 5.868 18.3487 5.5765 17.9992 5.5765L10.0189 5.58582C9.66517 5.58625 9.37863 5.87325 9.37863 6.22723V17.3494H2.64103V4.65723L9.37863 2.84471V4.3009H10.6607V2.00808C10.6607 1.59313 10.2542 1.28082 9.85321 1.38868L1.83355 3.54607C1.69734 3.58276 1.57702 3.66333 1.49123 3.7753C1.40544 3.88728 1.35895 4.02441 1.35897 4.16548V17.3494H0V18.6321H20V17.3494H10.6607Z"
                                                                        fill="#006CDD"
                                                                    />
                                                                    <path
                                                                        d="M17.3589 15.0853H18.641V16.2802H17.3589V15.0853ZM12.4625 8.52441H15.5177V9.80719H12.4625V8.52441ZM12.4625 11.4413H15.5177V12.7241H12.4625V11.4413ZM12.4625 14.3583H15.5177V15.6411H12.4625V14.3583Z"
                                                                        fill="#006CDD"
                                                                    />
                                                                </g>
                                                                <defs>
                                                                    <clipPath id="clip0_10991_55187">
                                                                        <rect width="20" height="20" fill="white" />
                                                                    </clipPath>
                                                                </defs>
                                                            </svg>
                                                        </div>
                                                        {option?.partsBrand?.name}
                                                    </Typography>
                                                    <Typography
                                                        padding={0}
                                                        sx={{
                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                            letterSpacing: '0.17px',
                                                            fontSize: '14px',
                                                            fontWeight: 400,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            minHeight: '20px',
                                                        }}
                                                        lineHeight={'normal'}
                                                    >
                                                        {' '}
                                                        <div style={{ width: '20px', height: '20px' }}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                                <path
                                                                    d="M16.6666 14.9997H3.33329V6.66634H16.6666M16.6666 4.99967H9.99996L8.33329 3.33301H3.33329C2.40829 3.33301 1.66663 4.07467 1.66663 4.99967V14.9997C1.66663 15.4417 1.84222 15.8656 2.15478 16.1782C2.46734 16.4907 2.89126 16.6663 3.33329 16.6663H16.6666C17.1087 16.6663 17.5326 16.4907 17.8451 16.1782C18.1577 15.8656 18.3333 15.4417 18.3333 14.9997V6.66634C18.3333 6.22431 18.1577 5.80039 17.8451 5.48783C17.5326 5.17527 17.1087 4.99967 16.6666 4.99967Z"
                                                                    fill="#006CDD"
                                                                />
                                                            </svg>
                                                        </div>
                                                        {option?.partsCategory?.name ? option?.partsCategory?.name : option?.tyreModel?.name}
                                                    </Typography>
                                                </Stack>
                                            </Grid>

                                            <Grid xs={4} md={4}>
                                                <Stack direction={'column'} gap={0.5} alignItems={'flex-end'}>
                                                    <Typography
                                                        padding={0}
                                                        sx={{
                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                            letterSpacing: '0.17px',
                                                            fontSize: '14px',
                                                            fontWeight: 400,
                                                            textAlign: 'right',
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            minHeight: '20px',
                                                        }}
                                                        lineHeight={'normal'}
                                                    >
                                                        Qty:{' '}
                                                        {/* {Number(option.inStock) +
                                                            Number(finalizedInventoryCount[option._id] || 0) -
                                                            Number(usedQuantityOfInventory[option._id] || 0)} */}
                                                        {NumericEngine.add(
                                                            Number(option?.inStock || 0),
                                                            Number(finalizedInventoryCount[option._id] || 0) - Number(usedQuantityOfInventory[option._id] || 0)
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        padding={0}
                                                        sx={{
                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                            letterSpacing: '0.17px',
                                                            fontSize: '14px',
                                                            fontWeight: 400,
                                                            textAlign: 'right',
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            minHeight: '20px',
                                                        }}
                                                        lineHeight={'normal'}
                                                    >
                                                        Cost: {NumericEngine.format(option?.lastCost || 0, { formatCurrency: true, trailingZeros: true })}
                                                        {/* {formatCurrency(option?.lastCost || 0)} */}
                                                    </Typography>
                                                    <Typography
                                                        padding={0}
                                                        sx={{
                                                            color: 'rgba(0, 0, 0, 0.87)',
                                                            letterSpacing: '0.17px',
                                                            fontSize: '14px',
                                                            fontWeight: 400,
                                                            textAlign: 'right',
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            minHeight: '20px',
                                                        }}
                                                        lineHeight={'normal'}
                                                    >
                                                        Retail:{' '}
                                                        {NumericEngine.format(option?.lastRetailPrice || 0, { formatCurrency: true, trailingZeros: true })}
                                                        {/* {formatCurrency(option?.lastRetailPrice || 0)} */}
                                                    </Typography>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </li>
                                </Box>
                                {/* {loader && (
                  <Box
                    component="li"
                    {...props}
                    style={{
                      padding: 0,
                      display: !loader ? "none" : "",
                    }}
                  >
                    {props["data-option-index"] + 1 == inventoryList?.length &&
                      loader && (
                        <li
                          style={{
                            width: "100%",
                            justifyContent: "center",
                            padding: 0,
                            display: "flex",
                          }}
                        >
                          <CircularProgress />
                        </li>
                      )}
                  </Box>
                )} */}
                            </>
                        );
                    } else if (loader && props.id === 'inventory-search-option-0') {
                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <CircularProgress />
                            </div>
                        );
                    }
                }}
                PopperComponent={(props) => (
                    <Popper
                        {...props}
                        style={{
                            width: '548px', // Set fixed width
                            maxWidth: '548px', // Ensure it doesn't exceed this width
                            zIndex: 1300, // Ensure it appears above other elements
                            borderRadius: '4px',
                            boxShadow: '0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 5px 5px -3px rgba(0, 0, 0, 0.20)',
                        }}
                        modifiers={[
                            {
                                name: 'flip',
                                enabled: false,
                            },
                        ]}
                        placement="bottom-start"
                    />
                )}
            />
            <BatchSelectionModal
                usedBatchQty={usedBatchQty}
                serviceListPayload={serviceList}
                outerIndex={outerIndex}
                innerIndex={innerIndex}
                open={openBatchSelectionModal}
                inventoryId={inventoryId}
                onClose={() => {
                    setInventoryId(null);
                    setBatchSelectionModal(false);
                }}
                onSave={(data) => {
                    const filteredData = data.filter((item) => item.isSelected);
                    if (filteredData.length > 0) {
                        let cloneService = JSON.parse(JSON.stringify(serviceList));
                        const partRowId = cloneService[outerIndex].parts[innerIndex].partRowId;
                        delete cloneService[outerIndex].parts[innerIndex].type;
                        const partCost = filteredData[0]?.batchCost || Number(NumericEngine.format(inventoryId?.lastRetailPrice || 0));

                        cloneService[outerIndex].parts[innerIndex] = {
                            ...inventoryId,
                            ...(partRowId ? { partRowId } : {}),
                        };
                        let totalPart = 0;
                        let totalQty = 0;

                        cloneService[outerIndex].parts[innerIndex].batchArray = filteredData.map((item, index) => {
                            totalPart = totalPart + NumericEngine.format(item.batchQty);
                            totalQty += Number(formatNumberAsWhole(item.batchQty)) || 0;
                            return {
                                outerIndex: outerIndex,
                                innerIndex: innerIndex,
                                batchIndex: index,
                                batchNumber: item.batchNumber,
                                batchQty: item.batchQty,
                                batchCost: item.batchCost,
                                inStock: item.inStock,
                            };
                        });
                        cloneService[outerIndex].parts[innerIndex].partQty = totalQty || 1;
                        cloneService[outerIndex].parts[innerIndex].partCost = partCost || 0;
                        cloneService[outerIndex].parts[innerIndex].inventoryType = INVENTORY_MANAGE_TYPE.MANAGED;

                        const qty = totalQty || 0;
                        const totalAmount = Number(qty) && Number(partCost) ? Number(NumericEngine.multiply(qty, Number(partCost))) : 0;

                        cloneService[outerIndex].parts[innerIndex].totalAmount = totalAmount;
                        setServiceList(cloneService);
                    }
                    setInventoryId(null);
                    setBatchSelectionModal(false);
                }}
            />
        </>
    );
};

export default GenralInventoryAutocomplete;
